import React, { useCallback } from "react";
import BlurredSuit from "../../assets/images/Blurred suit.png";
import BlurredShirt from "../../assets/images/Blurred shirt.png";

type AboutMeProps = {
  /** optional callbacks for CTAs — if not provided will fallback to anchors */
  onGetBlueprint?: () => void;
  onBookCall?: () => void;
  className?: string;
};

const BRAND = {
  navy: "#1B2951",
  gold: "#FFB000",
  white: "#FFFFFF",
  silver: "#8B9DC3",
  green: "#2D5A27",
};

const AboutMe: React.FC<AboutMeProps> = ({
  onGetBlueprint,
  onBookCall,
  className = "",
}) => {
  const handleGetBlueprint = useCallback(() => {
    if (onGetBlueprint) return onGetBlueprint();
    const el = document.getElementById("lead-magnet");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [onGetBlueprint]);

  const handleBookCall = useCallback(() => {
    if (onBookCall) return onBookCall();
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [onBookCall]);

  return (
    <section
      aria-labelledby="aboutme-heading"
      className={`relative overflow-hidden ${className}`}
      style={{
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: Visuals */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="relative w-[360px] max-w-full">
              {/* Background accent */}
              <div
                aria-hidden
                className="absolute -inset-3 rounded-2xl"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.gold} 100%)`,
                  filter: "blur(22px)",
                  opacity: 0.12,
                }}
              />
              <div className="relative rounded-2xl overflow-hidden border-2 border-white shadow-2xl">
                <img
                  src={BlurredSuit}
                  alt="Adeolu Adegbemi — The Blueprint Architect in navy blazer"
                  className="w-full h-auto object-cover block"
                />
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-gray-100">
                  <div className="text-xs text-[#1B2951] font-semibold">
                    Adeolu Adegbemi
                  </div>
                  <div className="text-[11px] text-gray-600">
                    The Blueprint Architect
                  </div>
                </div>
              </div>

              {/* Accent small image */}
              <div className="absolute -right-8 -bottom-8 w-36 h-36 rounded-xl overflow-hidden border-2 border-white shadow-lg">
                <img
                  src={BlurredShirt}
                  alt="Casual visual — navy shirt"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Right: Copy — benefits focused */}
          <div className="lg:col-span-7">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-3 mb-4">
                <span
                  className="px-3 py-1 rounded-full text-sm font-semibold"
                  style={{ background: "#EAF3FF", color: BRAND.navy }}
                >
                  The Blueprint Architect
                </span>
                <span className="text-sm text-gray-500">
                  Engineer • Pastor • Entrepreneur
                </span>
              </div>

              <h2
                id="aboutme-heading"
                className="text-3xl sm:text-4xl font-extrabold leading-tight"
                style={{ color: BRAND.navy }}
              >
                I help professionals build wealth without losing purpose.
              </h2>

              <p className="mt-4 text-lg text-gray-700">
                You already carry skill, calling, and credibility — but
                sometimes clarity, systems, and a repeatable plan are missing. I
                translate engineering precision into practical blueprints so you
                can grow income, steward resources wisely, and multiply your
                kingdom impact — without quitting your day job.
              </p>

              {/* Benefits list */}
              <ul className="mt-6 space-y-4">
                <li className="flex gap-4 items-start">
                  <div
                    className="flex-none w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ background: BRAND.navy }}
                    aria-hidden
                  >
                    1
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      Clarity & Confidence
                    </div>
                    <div className="text-sm text-gray-600">
                      Leave confusion behind—get a clear, step-by-step blueprint
                      that turns your idea into predictable progress.
                    </div>
                  </div>
                </li>

                <li className="flex gap-4 items-start">
                  <div
                    className="flex-none w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ background: BRAND.gold }}
                    aria-hidden
                  >
                    ⚙
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      Systems that Scale
                    </div>
                    <div className="text-sm text-gray-600">
                      Replace one-off hustle with repeatable processes — so your
                      impact and income grow while you sleep.
                    </div>
                  </div>
                </li>

                <li className="flex gap-4 items-start">
                  <div
                    className="flex-none w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ background: BRAND.green }}
                    aria-hidden
                  >
                    ✚
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      Stewardship & Purpose
                    </div>
                    <div className="text-sm text-gray-600">
                      Build wealth as a tool — fund your calling, support your
                      family, and create sustainable kingdom impact.
                    </div>
                  </div>
                </li>

                <li className="flex gap-4 items-start">
                  <div
                    className="flex-none w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ background: BRAND.silver }}
                    aria-hidden
                  >
                    ✓
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      Practical, Actionable Results
                    </div>
                    <div className="text-sm text-gray-600">
                      No fluff—every blueprint contains immediate next steps,
                      templates, and accountability paths proven by real
                      projects and church initiatives.
                    </div>
                  </div>
                </li>
              </ul>

              {/* Quick stats / social proof */}
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:gap-6 gap-4">
                <div className="bg-white border rounded-lg px-4 py-3 shadow-sm flex-1">
                  <div className="text-sm text-gray-500">Typical timeline</div>
                  <div
                    className="text-lg font-bold"
                    style={{ color: BRAND.navy }}
                  >
                    90 days to first measurable outcome
                  </div>
                </div>

                <div className="bg-white border rounded-lg px-4 py-3 shadow-sm flex-1">
                  <div className="text-sm text-gray-500">Proven result</div>
                  <div
                    className="text-lg font-bold"
                    style={{ color: BRAND.gold }}
                  >
                    Repeatable systems for income & stewardship
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleGetBlueprint}
                  className="inline-flex items-center justify-center gap-3 px-6 py-3 rounded-lg font-semibold shadow-md transition transform hover:-translate-y-0.5"
                  style={{
                    background: BRAND.gold,
                    color: BRAND.navy,
                    boxShadow: "0 6px 18px rgba(255,176,0,0.18)",
                  }}
                >
                  Get the 5-Step Blueprint
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke={BRAND.navy}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <button
                  onClick={handleBookCall}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold border-2"
                  style={{
                    borderColor: BRAND.navy,
                    color: BRAND.navy,
                    background: "white",
                  }}
                >
                  Book a 15-min Discovery Call
                </button>
              </div>

              <p className="mt-4 text-xs text-gray-500">
                No hype. No shortcuts. Just proven blueprints that help you
                create income, steward resources, and advance your calling. If
                you'd like, I can review your situation and give 3 quick wins
                during a brief audit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMe;
